/**
 * Gerenciador centralizado de profiles em localStorage
 * Reutilizável para Individual e LegalPerson
 */

export type EntityType = 'individual' | 'legal_person';

interface ProfileData {
    profiles: string[];
    timestamp: number;
    entityId: string;
    entityType: EntityType;
}

export class ProfileStorageManager {
    private static getKey(entityType: EntityType, entityId: string): string {
        return `${entityType}_profiles_edit_${entityId}`;
    }

    /**
     * Salva profiles no localStorage
     */
    static save(entityType: EntityType, entityId: string, profiles: string[]): boolean {
        try {
            // 🔍 DEBUG: Rejeitar completamente chamadas com entityId vazio
            if (!entityId || entityId.trim() === '') {
                console.error('🚨 ProfileStorageManager.save REJEITADO - entityId vazio!', { entityType, entityId, profiles });
                return false; // Rejeitar salvamento
            }
            
            const data: ProfileData = {
                profiles,
                timestamp: Date.now(),
                entityId,
                entityType
            };
            localStorage.setItem(this.getKey(entityType, entityId), JSON.stringify(data));
            return true;
        } catch (error) {
            console.warn('⚠️ Erro ao salvar profiles no localStorage:', error);
            return false;
        }
    }

    /**
     * Carrega profiles do localStorage com validação
     */
    static load(entityType: EntityType, entityId: string): string[] {
        try {
            const stored = localStorage.getItem(this.getKey(entityType, entityId));
            if (stored) {
                const data: ProfileData = JSON.parse(stored);
                
                // Validar dados
                if (data.entityId === entityId && data.entityType === entityType) {
                    // Verificar se não é muito antigo (1 hora)
                    const oneHour = 60 * 60 * 1000;
                    if (Date.now() - data.timestamp <= oneHour) {
                        return data.profiles || [];
                    } else {
                        console.log('⏰ Profiles expirados no localStorage');
                    }
                } else {
                    console.log('❌ Profiles inválidos no localStorage (ID/tipo diferente)');
                }
            }
        } catch (error) {
            console.warn('⚠️ Erro ao carregar profiles do localStorage:', error);
        }
        
        // Limpar se inválido
        this.clear(entityType, entityId);
        return [];
    }

    /**
     * Remove profiles específicos do localStorage
     */
    static clear(entityType: EntityType, entityId: string): void {
        const key = this.getKey(entityType, entityId);
        
        // 🚨 FIX: Só remover e logar se realmente existir
        if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
        }
    }

    /**
     * Limpeza automática de dados antigos
     */
    static cleanup(): void {
        const keys = Object.keys(localStorage);
        const oneHour = 60 * 60 * 1000;
        let cleaned = 0;
        
        keys.forEach(key => {
            if (key.includes('_profiles_edit_')) {
                try {
                    const data = JSON.parse(localStorage.getItem(key) || '');
                    if (Date.now() - data.timestamp > oneHour) {
                        localStorage.removeItem(key);
                        cleaned++;
                    }
                } catch (error) {
                    localStorage.removeItem(key);
                    cleaned++;
                }
            }
        });
        
        if (cleaned > 0) {
            console.log(`🧹 Limpeza automática: ${cleaned} profiles antigos removidos`);
        }
    }

    /**
     * Debug: listar todos os profiles armazenados
     */
    static debug(): void {
        const keys = Object.keys(localStorage);
        const profileKeys = keys.filter(key => key.includes('_profiles_edit_'));
        
        console.log('🔍 Debug ProfileStorageManager:');
        profileKeys.forEach(key => {
            try {
                const data = JSON.parse(localStorage.getItem(key) || '');
                const age = Math.round((Date.now() - data.timestamp) / 1000 / 60); // minutos
                console.log(`  ${key}: ${data.profiles.join(', ')} (${age}min atrás)`);
            } catch (error) {
                console.log(`  ${key}: INVÁLIDO`);
            }
        });
    }
}
